import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Button, Modal, Pagination, Table } from "antd";
import moment from "moment";

import productApi from "../../../apis/Product.api";
import { setLoading } from "../../../redux/store/common/commonDuck";

const ProductReviewRefComponent = ({
  visible,
  onClose,
  onSelect,
  currentProductId,
  selectedReviewProduct,
}) => {
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.common.loading);
  const setIsLoading = (value) => dispatch(setLoading(value));
  const user = useSelector((state) => state.auth.user);

  const [productItemList, setProductItemList] = useState([]);
  const [selectedRowKey, setSelectedRowKey] = useState([]);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalSize, setTotalSize] = useState(0);
  const [orderBy, setOrderBy] = useState("desc");
  const [orderType, setOrderType] = useState("created_at");

  useEffect(() => {
    if (visible) {
      setSelectedRowKey(
        selectedReviewProduct ? [selectedReviewProduct.product_id] : [],
      );
    }
  }, [visible, selectedReviewProduct]);

  const rowSelection = {
    type: "radio",
    selectedRowKeys: selectedRowKey, // selectedRowKey -> selectedRowKeys로 수정
    onChange: (selectedRowKeys, selectedRows) => {
      if (selectedRows[0]) {
        setSelectedRowKey(selectedRowKeys);
      }
    },
    getCheckboxProps: (record) => ({
      // 현재 수정 중인 상품의 ID와 같으면 선택 불가능
      disabled: record.product_id === parseInt(currentProductId),
      name: record.product_id,
    }),
  };

  const columns = [
    {
      title: "상품번호",
      width: 80,
      dataIndex: "product_id",
      key: "product_id",
      ellipsis: true,
    },
    {
      title: "카테고리",
      width: 120,
      dataIndex: "product_category_name",
      key: "product_category_name",
      ellipsis: true,
      render: (value) => value || "-",
    },
    {
      title: "브랜드명",
      width: 130,
      dataIndex: "product_brand_name",
      key: "product_brand_name",
      ellipsis: true,
    },
    {
      title: "상품명",
      width: 150,
      dataIndex: "product_name",
      key: "product_name",
      ellipsis: true,
    },
    {
      title: "상품 상태",
      width: 100,
      dataIndex: "is_purchase",
      key: "is_purchase",
      ellipsis: true,
      render: (value, record) => {
        if (record.quantity === 0) return "품절";
        return value ? "판매중" : "미판매";
      },
    },
    {
      title: "노출여부",
      width: 100,
      dataIndex: "is_admin_product",
      key: "is_admin_product",
      ellipsis: true,
      render: (value, record) => {
        if (value) {
          return "미노출";
        } else {
          return "노출";
        }
      },
    },
    {
      title: "판매가",
      width: 90,
      dataIndex: "price_1",
      key: "price_1",
      ellipsis: true,
      render: (value) => <span>{value?.toLocaleString()}</span>,
    },
    {
      title: "등록일",
      width: 160,
      dataIndex: "created_at",
      key: "created_at",
      ellipsis: true,
      render: (value) =>
        value ? moment(value).format("YYYY.MM.DD. HH:mm") : "-",
    },
  ];

  useEffect(() => {
    readProductList();
  }, [offset, pageSize, orderType, orderBy]);

  const readProductList = async () => {
    setIsLoading(true);
    try {
      const res = await productApi.readProductList({
        product_id: 0,
        product_maker_name: "",
        product_name: "",
        product_brand_name: "",
        product_status: "total",
        category1: "",
        category2: "",
        category3: "",
        orderType,
        orderBy,
        offset,
        pageSize,
        userId: user.id === 724 ? 1 : user.id,
      });

      if (res.code === 200) {
        setProductItemList(res.result.productItemMasterList);
        setTotalSize(res.result.totalCnt);
      } else {
        alert("정상적인 요청이 아닙니다.");
      }
    } catch (error) {
      console.log("readProductList read error", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePagination = (pageNumber) => {
    const offset = (pageNumber - 1) * pageSize;
    setCurrentPageNumber(pageNumber);
    setOffset(offset);
  };

  const pageSizeChange = (current, pageSize) => {
    setPageSize(pageSize);
    setOffset(0);
    setCurrentPageNumber(1);
  };

  const handleSelect = () => {
    const selectedProduct = productItemList.find(
      (item) => item.product_id === selectedRowKey[0],
    );
    if (selectedProduct) {
      onSelect(selectedProduct);
      onClose();
    }
  };

  const onRow = (record) => ({
    onClick: () => {
      // 현재 수정 중인 상품이 아닐 때만 선택 가능
      if (record.product_id !== parseInt(currentProductId)) {
        setSelectedRowKey([record.product_id]);
      }
    },
    style: {
      cursor:
        record.product_id === parseInt(currentProductId)
          ? "not-allowed"
          : "pointer",
      backgroundColor:
        record.product_id === parseInt(currentProductId)
          ? "#f5f5f5"
          : "inherit",
      color:
        record.product_id === parseInt(currentProductId) ? "#999" : "inherit",
    },
  });

  return (
    <Modal
      title="상품 선택"
      visible={visible}
      onCancel={onClose}
      width={1200}
      footer={[
        <Button key="cancel" onClick={onClose}>
          취소
        </Button>,
        <Button
          key="select"
          type="primary"
          onClick={handleSelect}
          disabled={!selectedRowKey.length}
        >
          선택
        </Button>,
      ]}
    >
      <div style={{ padding: "20px 0" }}>
        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={productItemList}
          loading={isLoading}
          bordered
          size="middle"
          rowKey="product_id"
          scroll={{ x: "calc(700px + 50%)", y: "50vh" }}
          pagination={false}
          onRow={onRow}
        />

        <div style={{ textAlign: "right", marginTop: 16 }}>
          <Pagination
            showQuickJumper
            showSizeChanger
            onShowSizeChange={pageSizeChange}
            total={totalSize}
            onChange={handleChangePagination}
            current={currentPageNumber}
          />
        </div>
      </div>
    </Modal>
  );
};

export default ProductReviewRefComponent;
